import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, RefreshCw } from "lucide-react";
import Seo from "@/components/Seo";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "scwc_admin_token";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso;
    }
}

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
    const [authed, setAuthed] = useState(false);
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [loginError, setLoginError] = useState("");
    const [loggingIn, setLoggingIn] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    const authHeader = { headers: { Authorization: `Bearer ${token}` } };

    const fetchEnquiries = async (t = token) => {
        setLoadingList(true);
        try {
            const { data } = await axios.get(`${API}/enquiries`, {
                headers: { Authorization: `Bearer ${t}` },
            });
            setEnquiries(data);
        } catch {
            toast.error("Could not load enquiries.");
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        if (!token) {
            setChecking(false);
            return;
        }
        axios
            .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                setAuthed(true);
                fetchEnquiries(token);
            })
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
            })
            .finally(() => setChecking(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLogin = async (e) => {
        e.preventDefault();
        setLoginError("");
        setLoggingIn(true);
        try {
            const { data } = await axios.post(`${API}/auth/login`, loginForm);
            localStorage.setItem(TOKEN_KEY, data.access_token);
            setToken(data.access_token);
            setAuthed(true);
            fetchEnquiries(data.access_token);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setLoginError(typeof detail === "string" ? detail : "Login failed. Please try again.");
        } finally {
            setLoggingIn(false);
        }
    };

    const onLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setEnquiries([]);
    };

    const onStatusChange = async (enquiryId, status) => {
        try {
            await axios.patch(`${API}/enquiries/${enquiryId}/status`, { status }, authHeader);
            setEnquiries((list) => list.map((e) => (e.enquiry_id === enquiryId ? { ...e, status } : e)));
        } catch {
            toast.error("Could not update status.");
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-page">
            <Seo title="Admin | The Sens Civil Works Consultancy" description="Admin area." />
            {checking ? (
                <div className="flex min-h-screen items-center justify-center">
                    <Loader2 className="animate-spin text-navy" size={32} />
                </div>
            ) : !authed ? (
                <div className="flex min-h-screen items-center justify-center px-6">
                    <form
                        onSubmit={onLogin}
                        className="w-full max-w-sm border border-slate-200 bg-white p-10"
                        data-testid="admin-login-form"
                    >
                        <div className="flex justify-center">
                            <LogoMark size={48} />
                        </div>
                        <h1 className="mt-6 text-center font-display text-xl font-bold text-navy">Admin Login</h1>
                        <p className="mt-1 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Enquiries Dashboard
                        </p>
                        <div className="mt-8 space-y-5">
                            <div>
                                <Label htmlFor="admin-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                    Email
                                </Label>
                                <Input
                                    id="admin-email"
                                    type="email"
                                    value={loginForm.email}
                                    onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                                    className="mt-2 rounded-none border-slate-300"
                                    data-testid="admin-email-input"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="admin-password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                    Password
                                </Label>
                                <Input
                                    id="admin-password"
                                    type="password"
                                    value={loginForm.password}
                                    onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                                    className="mt-2 rounded-none border-slate-300"
                                    data-testid="admin-password-input"
                                    required
                                />
                            </div>
                            {loginError && (
                                <p className="text-sm text-red-600" data-testid="admin-login-error">
                                    {loginError}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={loggingIn}
                                className="flex w-full items-center justify-center gap-2 bg-navy px-6 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-amber disabled:opacity-60"
                                data-testid="admin-login-submit"
                            >
                                {loggingIn ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                                {loggingIn ? "Signing in..." : "Sign In"}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <LogoMark size={44} />
                            <div>
                                <h1 className="font-display text-2xl font-bold tracking-tight text-navy">
                                    Enquiries Dashboard
                                </h1>
                                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Contact form submissions
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => fetchEnquiries()}
                                className="flex items-center gap-2 border border-slate-300 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-navy transition-colors duration-200 hover:border-navy"
                                data-testid="admin-refresh-btn"
                            >
                                <RefreshCw size={13} /> Refresh
                            </button>
                            <button
                                onClick={onLogout}
                                className="flex items-center gap-2 bg-navy px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-amber"
                                data-testid="admin-logout-btn"
                            >
                                <LogOut size={13} /> Logout
                            </button>
                        </div>
                    </div>

                    <div className="mt-10 border border-slate-200 bg-white" data-testid="enquiries-table-wrapper">
                        {loadingList ? (
                            <div className="flex items-center justify-center py-20">
                                <Loader2 className="animate-spin text-navy" size={28} />
                            </div>
                        ) : enquiries.length === 0 ? (
                            <p className="py-20 text-center text-sm text-slate-500" data-testid="enquiries-empty">
                                No enquiries yet. New contact-form submissions will appear here.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table data-testid="enquiries-table">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Reference</TableHead>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Date</TableHead>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Name</TableHead>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Contact</TableHead>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Service</TableHead>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Message</TableHead>
                                            <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em]">Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {enquiries.map((e) => (
                                            <TableRow key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`}>
                                                <TableCell className="font-mono text-xs font-semibold text-navy">
                                                    {e.enquiry_id}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap text-xs text-slate-500">
                                                    {formatDate(e.created_at)}
                                                </TableCell>
                                                <TableCell className="text-sm font-medium text-navy">
                                                    {e.name}
                                                    <span className="block text-xs font-normal text-slate-500">{e.location}</span>
                                                </TableCell>
                                                <TableCell className="text-xs text-slate-600">
                                                    {e.phone}
                                                    <span className="block">{e.email}</span>
                                                </TableCell>
                                                <TableCell className="text-xs text-slate-600">
                                                    {e.service_required}
                                                    <span className="block text-slate-400">{e.project_type}</span>
                                                </TableCell>
                                                <TableCell className="max-w-[260px] text-xs text-slate-600">
                                                    <span className="line-clamp-3">{e.message}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={e.status}
                                                        onValueChange={(v) => onStatusChange(e.enquiry_id, v)}
                                                    >
                                                        <SelectTrigger
                                                            className="h-8 w-32 rounded-none text-xs"
                                                            data-testid={`enquiry-status-${e.enquiry_id}`}
                                                        >
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent className="rounded-none">
                                                            <SelectItem value="new">New</SelectItem>
                                                            <SelectItem value="in-progress">In Progress</SelectItem>
                                                            <SelectItem value="closed">Closed</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
